import React, { createContext, useContext, useMemo, useRef } from "react";

export interface FieldGroupContextValue {
  /**
   * ローディング状態であることを示す値
   */
  loading?: boolean;
  /**
   * 読み取り専用であることを示す値
   */
  readOnly?: boolean;
  onLoaded?: () => void;
}

const FieldGroupContext = createContext<FieldGroupContextValue>({});

export const useFieldGroup = (argsValue: FieldGroupContextValue) => {
  const contextValue = useContext(FieldGroupContext);

  const prevLoading = useRef<boolean | undefined>(undefined);

  const loading = useMemo(() => {
    const next =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      argsValue.loading === undefined
        ? contextValue.loading
        : argsValue.loading;
    // eslint-disable-next-line react-hooks/refs
    if (prevLoading.current === true && next === false) {
      argsValue.onLoaded?.();
    }
    // eslint-disable-next-line react-hooks/refs
    prevLoading.current = next;
    return next;
  }, [argsValue, contextValue.loading]);

  const value = useMemo(() => {
    return {
      loading,
      readOnly:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        argsValue.readOnly === undefined
          ? contextValue.readOnly
          : argsValue.readOnly,
    };
  }, [argsValue.readOnly, contextValue.readOnly, loading]);

  return value;
};

export interface FieldGroupProps extends FieldGroupContextValue {
  children?: React.ReactNode;
}

/**
 * FieldGroupは、フォーム用のコンポーネントに対して、特定の属性をまとめて与えるための実態のないコンポーネントです。
 *
 * 子孫のフォーム要素に同種の属性がついている場合は、子孫側の指定が優先されます。
 */
export const FieldGroup = ({
  children,
  loading,
  readOnly,
}: FieldGroupProps) => {
  const value = useMemo(() => {
    return {
      loading,
      readOnly,
    };
  }, [loading, readOnly]);

  return (
    <FieldGroupContext.Provider value={value}>
      {children}
    </FieldGroupContext.Provider>
  );
};
